'use client';

import React, { useMemo, useState } from 'react';
import RecipesFilterBar from './RecipesFilterBar';
import RecipesSidebar from './RecipesSidebar';
import RecipesGrid from './RecipesGrid';
import Pagination from '@/components/ui/Pagination';
import { Recipe } from '@/types/recipe';
import { PaginationInfo } from '@/types/pagination';

interface RecipesPageContentProps {
  recipes: Recipe[];
  sidebarSections: Record<string, string[]>;
  categoryColors: Record<string, string>;
  filterOptions: { value: string; label: string }[];
  sortOptions: { value: string; label: string }[];
  paginationInfo?: PaginationInfo;


  className?: string;
}

const RecipesPageContent: React.FC<RecipesPageContentProps> = ({
  recipes,
  sidebarSections,
  categoryColors,
  filterOptions,
  sortOptions,
  paginationInfo,
  className = ''
}) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState<string>('published_desc');
  const [search, setSearch] = useState('');
  const [selectedSidebarItems, setSelectedSidebarItems] = useState<Record<string, string[]>>({});

  const getCurrentSortLabel = () => {
    return sortOptions.find(o => o.value === sortBy)?.label ?? 'Relevancia';
  };

  const handleChipFilterClick = (value: string) => {
    setSelectedFilters(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const handleClearAllFilters = () => setSelectedFilters([]);

  const handleSidebarItemClick = (section: string, item: string) => {
    setSelectedSidebarItems((prev) => {
      const current = prev[section] ?? [];
      const exists = current.includes(item);
      const nextSectionItems = exists ? current.filter((value) => value !== item) : [...current, item];
      return { ...prev, [section]: nextSectionItems };
    });
  };

  const normalizeText = (value: string): string =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const parseDurationToMinutes = (value: string): number | null => {
    const normalized = normalizeText(value);
    if (!normalized) return null;

    // Soporta formatos: "30 min", "1h 45 min", "2 h", etc.
    const hourMatch = normalized.match(/(\d+)\s*h/);
    const minuteMatch = normalized.match(/(\d+)\s*min/);

    const hours = hourMatch ? Number(hourMatch[1]) : 0;
    const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;

    const total = hours * 60 + minutes;
    if (total > 0) return total;

    // fallback: cuando viene solo número suelto
    const plainNumber = normalized.match(/^\d+$/);
    return plainNumber ? Number(plainNumber[0]) : null;
  };

  const matchDurationBucket = (recipeMinutes: number | null, selectedBucket: string): boolean => {
    if (recipeMinutes === null) return false;
    const bucket = normalizeText(selectedBucket);

    if (bucket.includes('< 30')) return recipeMinutes < 30;
    if (bucket.includes('30-60')) return recipeMinutes >= 30 && recipeMinutes <= 60;
    if (bucket.includes('> 60')) return recipeMinutes > 60;

    return false;
  };

  const filteredRecipes = useMemo(() => {
    let list = [...recipes];
    // Chips superiores: filtrar por categoriaPrincipal (slug de categories)
    if (selectedFilters.length > 0) {
      list = list.filter((r) =>
        selectedFilters.some((f) => (r.categorySlug ?? '').toLowerCase() === f.toLowerCase())
      );
    }

    // Sidebar "Platos": filtrar por tags de Strapi asignados a la receta
    const selectedPlatos = selectedSidebarItems.Platos ?? [];
    if (selectedPlatos.length > 0) {
      list = list.filter((recipe) => {
        const recipeTags = (recipe.tags ?? []).map((tag) => tag.toLowerCase());
        return selectedPlatos.some((selected) => recipeTags.includes(selected.toLowerCase()));
      });
    }

    // Sidebar "Dificultad": filtrar por dificultad de receta
    const selectedDifficulties = selectedSidebarItems.Dificultad ?? [];
    if (selectedDifficulties.length > 0) {
      list = list.filter((recipe) => {
        const recipeDifficulty = normalizeText(recipe.difficulty ?? '');
        return selectedDifficulties.some(
          (selected) => recipeDifficulty === normalizeText(selected)
        );
      });
    }

    // Sidebar "Duración": filtrar por rango de minutos
    const selectedDurations = selectedSidebarItems.Duración ?? [];
    if (selectedDurations.length > 0) {
      list = list.filter((recipe) => {
        const recipeMinutes = parseDurationToMinutes(recipe.time ?? '');
        return selectedDurations.some((durationBucket) =>
          matchDurationBucket(recipeMinutes, durationBucket)
        );
      });
    }

    // ordenar
    const toTimestamp = (value?: string): number => {
      if (!value) return 0;
      const ts = new Date(value).getTime();
      return Number.isNaN(ts) ? 0 : ts;
    };

    switch (sortBy) {
      case 'name':
        list.sort((a, b) => (a.shortTitle ?? a.title).localeCompare(b.shortTitle ?? b.title));
        break;
      case 'published_asc':
        list.sort((a, b) => toTimestamp(a.publishedAtRaw) - toTimestamp(b.publishedAtRaw));
        break;
      case 'published_desc':
        list.sort((a, b) => toTimestamp(b.publishedAtRaw) - toTimestamp(a.publishedAtRaw));
        break;
      default:
        list.sort((a, b) => toTimestamp(b.publishedAtRaw) - toTimestamp(a.publishedAtRaw));
    }
    return list;
  }, [recipes, selectedFilters, sortBy, selectedSidebarItems]);

  return (
    <div data-component-id="src/components/sections/recipes/RecipesPageContent.tsx:158:4" data-component-path="src/components/sections/recipes/RecipesPageContent.tsx" data-component-line="158" data-component-file="RecipesPageContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`bg-global-10 rounded-[20px] mx-4 mt-2 ${className}`}>
      <RecipesFilterBar data-component-id="src/components/sections/recipes/RecipesPageContent.tsx:159:6" data-component-path="src/components/sections/recipes/RecipesPageContent.tsx" data-component-line="159" data-component-file="RecipesPageContent.tsx" data-component-name="RecipesFilterBar" data-component-content="%7B%22elementName%22%3A%22RecipesFilterBar%22%7D"
        selectedFilters={selectedFilters}
        onChipClick={handleChipFilterClick}
        sortBy={sortBy}
        onSortChange={setSortBy}
        showSortDropdown={showSortDropdown}
        onToggleSortDropdown={() => setShowSortDropdown(!showSortDropdown)}
        sortOptions={sortOptions}
        getCurrentSortLabel={getCurrentSortLabel}
        filterOptions={filterOptions}
      />

      <div data-component-id="src/components/sections/recipes/RecipesPageContent.tsx:171:6" data-component-path="src/components/sections/recipes/RecipesPageContent.tsx" data-component-line="171" data-component-file="RecipesPageContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%22%7D" className="flex flex-row">
        <RecipesSidebar data-component-id="src/components/sections/recipes/RecipesPageContent.tsx:172:8" data-component-path="src/components/sections/recipes/RecipesPageContent.tsx" data-component-line="172" data-component-file="RecipesPageContent.tsx" data-component-name="RecipesSidebar" data-component-content="%7B%22elementName%22%3A%22RecipesSidebar%22%7D"
          sections={sidebarSections}
          search={search}
          onSearchChange={setSearch}
          selectedItemsBySection={selectedSidebarItems}
          onSectionItemClick={handleSidebarItemClick}
        />

        <div data-component-id="src/components/sections/recipes/RecipesPageContent.tsx:180:8" data-component-path="src/components/sections/recipes/RecipesPageContent.tsx" data-component-line="180" data-component-file="RecipesPageContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20p-6%22%7D" className="flex-1 p-6">
          <RecipesGrid data-component-id="src/components/sections/recipes/RecipesPageContent.tsx:181:10" data-component-path="src/components/sections/recipes/RecipesPageContent.tsx" data-component-line="181" data-component-file="RecipesPageContent.tsx" data-component-name="RecipesGrid" data-component-content="%7B%22elementName%22%3A%22RecipesGrid%22%7D" recipes={filteredRecipes} categoryColors={categoryColors} search={search} />
        </div>
      </div>

      {paginationInfo && (
        <div data-component-id="src/components/sections/recipes/RecipesPageContent.tsx:186:8" data-component-path="src/components/sections/recipes/RecipesPageContent.tsx" data-component-line="186" data-component-file="RecipesPageContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-8%20mb-6%22%7D" className="mt-8 mb-6">
          <Pagination data-component-id="src/components/sections/recipes/RecipesPageContent.tsx:187:10" data-component-path="src/components/sections/recipes/RecipesPageContent.tsx" data-component-line="187" data-component-file="RecipesPageContent.tsx" data-component-name="Pagination" data-component-content="%7B%22elementName%22%3A%22Pagination%22%7D" paginationInfo={paginationInfo} handlePageChange={() => {}} />
        </div>
      )}
    </div>
  );
};

export default RecipesPageContent;


